/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function () {
  'use strict';

  const FEE_VARIANT_ID = 48973403488409;
  const NTC_PROPERTY_NAME = 'Add NTC';
  const NTC_PROPERTY_VALUE = 'Yes';
  const FEE_MARKER_NAME = '_YDL NTC Fee';
  const FEE_MARKER_VALUE = 'true';
  const SYNC_DELAY_MS = 120;

  let syncTimer = null;
  let activeSync = null;
  let rerunRequested = false;

  const cartEndpoint = (action) => {
    const cartUrl = window.routes && window.routes.cartUrl
      ? window.routes.cartUrl
      : '/cart';

    return action ? `${cartUrl}/${action}.js` : `${cartUrl}.js`;
  };

  const cartRequest = async (action, options) => {
    const response = await fetch(cartEndpoint(action), {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`NTC fee cart request failed (${response.status})`);
    }

    return response.json();
  };

  const ntcQuantity = (cart) => cart.items.reduce((total, item) => {
    const hasNtc = item.variant_id !== FEE_VARIANT_ID
      && item.properties
      && item.properties[NTC_PROPERTY_NAME] === NTC_PROPERTY_VALUE;

    return hasNtc ? total + item.quantity : total;
  }, 0);

  const updateFeeLines = async (cart, requiredQuantity) => {
    const feeLines = cart.items.filter((item) => item.variant_id === FEE_VARIANT_ID);

    if (requiredQuantity > 0 && feeLines.length === 0) {
      await cartRequest('add', {
        method: 'POST',
        body: JSON.stringify({
          items: [{
            id: FEE_VARIANT_ID,
            quantity: requiredQuantity,
            properties: {
              [FEE_MARKER_NAME]: FEE_MARKER_VALUE
            }
          }]
        })
      });

      return true;
    }

    if (feeLines.length === 0) {
      return false;
    }

    const markedFeeLine = feeLines.find((item) => (
      item.properties && item.properties[FEE_MARKER_NAME] === FEE_MARKER_VALUE
    ));
    const retainedFeeLine = markedFeeLine || feeLines[0];
    const updates = {};

    if (retainedFeeLine.quantity !== requiredQuantity) {
      updates[retainedFeeLine.key] = requiredQuantity;
    }

    feeLines.forEach((item) => {
      if (item.key !== retainedFeeLine.key) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) {
      return false;
    }

    await cartRequest('update', {
      method: 'POST',
      body: JSON.stringify({ updates })
    });

    return true;
  };

  const refreshCartDisplay = () => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { scrollToTop: false }
    }));
  };

  const synchronizeNtcFee = () => {
    if (activeSync) {
      rerunRequested = true;
      return activeSync;
    }

    activeSync = (async () => {
      const cart = await cartRequest('', { method: 'GET' });
      const changed = await updateFeeLines(cart, ntcQuantity(cart));

      if (changed) {
        refreshCartDisplay();
      }
    })()
      .catch((error) => {
        console.error('[YDL NTC fee]', error);
      })
      .finally(() => {
        activeSync = null;

        if (rerunRequested) {
          rerunRequested = false;
          scheduleSync();
        }
      });

    return activeSync;
  };

  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(synchronizeNtcFee, SYNC_DELAY_MS);
  }

  document.addEventListener('product:added', scheduleSync);
  document.addEventListener('cart:rerendered', scheduleSync);
  document.addEventListener('DOMContentLoaded', scheduleSync);
  window.addEventListener('pageshow', scheduleSync);
})();
